import { useEffect } from 'react'

// Module-level reference count so nested/overlapping locks (e.g. a preview popper
// closing as a dialog opens) don't clobber each other. Only the first lock writes
// the body styles; only the last release restores them.
let lockCount = 0
let savedScrollY = 0

/**
 * Locks background scrolling while `active` is true.
 *
 * MUI's built-in scroll lock (disableScrollLock) is disabled across the app because it
 * adds padding-right to the body to compensate for the scrollbar, which conflicts with
 * scrollbarGutter: 'stable' on html and causes a white bar / layout shift. We handle
 * scroll prevention ourselves using position:fixed on the body, and tint the html
 * background so the scrollbar gutter matches the backdrop (no shift, only color change).
 */
export function useBodyScrollLock(active: boolean) {
  useEffect(() => {
    if (!active) return

    if (lockCount === 0) {
      savedScrollY = window.scrollY
      document.body.style.position = 'fixed'
      document.body.style.top = `-${savedScrollY}px`
      document.body.style.width = '100%'
      document.body.style.minHeight = '100vh'
      // Smoothly tint the html background so the scrollbar gutter matches the backdrop.
      // The gutter always stays (no shift), only its color changes.
      document.documentElement.style.transition = 'background-color 225ms'
      document.documentElement.style.backgroundColor = 'rgba(0, 0, 0, 0.5)'
    }
    lockCount++

    return () => {
      lockCount = Math.max(0, lockCount - 1)
      if (lockCount > 0) return

      const scrollY = savedScrollY
      // Transition back to the normal page background color before unfixing.
      document.documentElement.style.backgroundColor = ''
      // Wait for the transition to finish before restoring body styles.
      setTimeout(() => {
        if (typeof document === 'undefined') return
        document.body.style.position = ''
        document.body.style.top = ''
        document.body.style.width = ''
        document.body.style.minHeight = ''
        document.documentElement.style.transition = ''
        window.scrollTo(0, scrollY)
      }, 225)
    }
  }, [active])
}
